/** localStorage that never throws (private mode, blocked storage, quota). */
export function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function save(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable: the game still plays, it just won't resume */
  }
}

export function remove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export const KEYS = {
  name: 'ancientnine.name',
  solo: 'ancientnine.solo',
  soloSetup: 'ancientnine.soloSetup',
  lastRoom: 'ancientnine.lastRoom',
  muted: 'ancientnine.muted',
  tapTwice: 'ancientnine.tapTwice',
  onlineHoles: 'ancientnine.onlineHoles',
  /** First-game coach done or skipped (REDESIGN.md 7.10). */
  coach: 'nn.coach.v1',
  /** Set once the player has finished a hole. */
  holeDone: 'ancientnine.holeDone',
  /** World numbers whose travel flavor line this device has shown. */
  worldsSeen: 'ancientnine.worldsSeen',
  skipTravel: 'ancientnine.skipTravel',
  reduceMotion: 'ancientnine.reduceMotion',
  /** Tibbs's last-card tip: games it has shown in, and games it was closed in (game/lastCardTip.ts). */
  lastCardTip: 'ancientnine.lastCardTip',
  /** The in-game "Hint button" switch, vs computer (default on; docs/HINTS-LAYOUT-TUTORIAL.md A1). */
  hints: 'ancientnine.hints',
} as const;

/**
 * The game was called Niners until 2026-11; its keys were `niners.*`. Copy each
 * old value to its new key once (only when the new key is empty), then drop the
 * old key, so a saved game, name and settings survive the rename. Covers the
 * player id and the tuner's keys too, which live outside KEYS.
 */
const LEGACY_PREFIX = 'niners.';
const PREFIX = 'ancientnine.';
let migrated = false;

export function migrateLegacyKeys(store: Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'> | null = safeLocalStorage()): void {
  if (migrated || !store) return;
  migrated = true;
  try {
    const old: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      if (k?.startsWith(LEGACY_PREFIX)) old.push(k);
    }
    for (const k of old) {
      const next = PREFIX + k.slice(LEGACY_PREFIX.length);
      const value = store.getItem(k);
      if (value !== null && store.getItem(next) === null) store.setItem(next, value);
      store.removeItem(k);
    }
  } catch {
    /* storage unavailable or full: nothing to migrate, or keep the old keys for next time */
    migrated = false;
  }
}

/** Test hook: allow migrateLegacyKeys to run again. */
export function resetLegacyMigration(): void {
  migrated = false;
}

function safeLocalStorage(): Storage | null {
  try {
    // Through window, so Node (tests, the Worker build) never touches its experimental localStorage global.
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

migrateLegacyKeys();
